import { ResourceDetailFeatureTaskEditorProps, SubmitButton, TextField } from '@cibo/ui'
import { Stack, Typography } from '@mui/material'
import { Field, Form, Formik, FormikHelpers } from 'formik'
import { useSnackbar } from 'notistack'
import { propEq } from 'ramda'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { useProgramEngagementContext } from '../../../../context'
import { useProgramEngagementDetails, useUpdateProgramEngagementDetails } from '../../../../queries'
import { RDEqipContractResults } from './types'

export const EqipContractResultsTaskEditor = ({
  detailRequirements,
  engagementId,
}: ResourceDetailFeatureTaskEditorProps<RDEqipContractResults>) => {
  const { t } = useTranslation('@cibo/programs/EqipContractResultsTaskEditor')
  const { enqueueSnackbar } = useSnackbar()
  const { engagement } = useProgramEngagementContext()
  const { advisorName, advisorEmail } = engagement.data?.copyReplacementTokens || {}

  const details = useProgramEngagementDetails({
    // @ts-ignore faking a requirement here
    detailRequirements,
    resourceId: engagementId,
  })
  const detail = details.data?.find(propEq('traitId', 'eqipContractResults'))

  const detailValue = detail?.value as {
    csvContractInfo: string
    ciboUserName: string
    ciboUserEmail: string
    advisorName: string
    advisorEmail: string
  }

  const initialValues = useMemo(
    () => ({
      csvContractInfo: detailValue?.csvContractInfo || '',
      ciboUserName: detailValue?.ciboUserName || '',
      ciboUserEmail: detailValue?.ciboUserEmail || '',
      advisorName: detailValue?.advisorName || advisorName || '',
      advisorEmail: detailValue?.advisorEmail || advisorEmail || '',
    }),
    [detail, advisorName, advisorEmail]
  )

  const updateDetail = useUpdateProgramEngagementDetails()

  const handleSubmit = async (values: any, { setSubmitting }: FormikHelpers<any>) => {
    await updateDetail
      .mutateAsync({
        resourceId: engagementId,
        details: [
          // @ts-ignore this "value" shape detail is not like the established input+result ones
          {
            traitId: 'eqipContractResults',
            year: detailRequirements[0].year,
            value: values,
          },
        ],
      })
      .then(() => {
        enqueueSnackbar(t('success'), { variant: 'success' })
      })
      .catch((error: any) => {
        enqueueSnackbar(error.message, { variant: 'error' })
      })

    setSubmitting(false)
  }

  return (
    <Stack spacing={3}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={Yup.object().shape({
          csvContractInfo: Yup.string().required(t('required')).min(1, 'Must have some csv data'),
          ciboUserName: Yup.string().matches(/\S+\s+\S+/, 'Must contain a first and last name'),
          ciboUserEmail: Yup.string().email().required(t('required')),
          advisorName: Yup.string().matches(/\S+\s+\S+/, 'Must contain a first and last name'),
          advisorEmail: Yup.string().email().required(t('required')),
        })}
      >
        <Form>
          <Stack spacing={1.5}>
            <Typography variant="h6">{t('eqipContractResults')}</Typography>

            <Typography>{t('eqipContractResultsDescription')}</Typography>
            <Field name="csvContractInfo" component={TextField} multiline rows={10} fullWidth />

            <Field name="ciboUserName" component={TextField} label={t('ciboUserName')} />

            <Field name="ciboUserEmail" component={TextField} label={t('ciboUserEmail')} />

            <Field name="advisorName" component={TextField} label={t('advisorName')} />

            <Field name="advisorEmail" component={TextField} label={t('advisorEmail')} />

            <Stack direction={'row'} spacing={2} justifyContent="flex-end">
              <SubmitButton>{t('save')}</SubmitButton>
            </Stack>
          </Stack>
        </Form>
      </Formik>
    </Stack>
  )
}
